import React, { useEffect, useRef, useState } from 'react';
import {
  Calendar,
  FilePlus,
  Folder,
  ChevronDown,
  Check,
  LayoutDashboard,
  Menu,
  X,
  LogOut,
  User2,
  Key,
  User,
} from 'lucide-react';
import { logout } from '@/store';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useStep } from '@/context/StepProvider';
import { useLogoutMutation } from '@/store/api/authApi';
import { toast } from 'react-toastify';

const navItems = [
  { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { name: 'Event', icon: Calendar, path: '/event-list' },
  // { name: 'Create Certificate', icon: FilePlus, path: '/create-certificate' },
  { name: 'Manage Certificates', icon: Folder, path: '/certificate-list' },
];

export const DashboardHeader: React.FC = () => {
  const [active, setActive] = useState('Dashboard');
  const [openDropdown, setOpenDropdown] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const handleLogout = () => {
  //   Cookies.remove('authToken');
  //   dispatch(logout());
  //   navigate('/login', { replace: true });
  // };
  const [logoutApi] = useLogoutMutation();
  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
      dispatch(logout());
      navigate('/login', { replace: true });
      toast.success('Logged out successfully');
    } catch (err) {
      console.error('Logout failed', err);
    }
  };
  const location = useLocation();
  const { step } = useStep();
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const pageContentMap: Record<string, any> = {
    '/': {
      title: 'Dashboard',
      subtitle: 'Welcome to your certificate management system',
      buttonText: 'Create New Event',
      buttonAction: '/create-event',
      showButton: true,
    },

    '/certificates': {
      title: 'Manage Certificates',
      subtitle: 'View, search, and download your certificates',
      showButton: false,
    },

    '/create-certificate': {
      title: 'Create Certificate',
      subtitle: 'Follow the steps to generate certificates.',
      showButton: false,
      type: 'stepper',
    },
  };
  const currentPage = pageContentMap[location.pathname] || pageContentMap['/'];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <div className="w-full rounded-b-[40px] bg-primary-gradient text-white p-4 lg:p-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-12">
          <div className="flex items-center gap-3">
            <img src="/images/mstlogo.svg" alt="logo" onClick={() => navigate('/')} />
          </div>
          <div className="hidden lg:flex items-center gap-8 text-sm">
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <div
                  key={index}
                  onClick={() => {
                    // setActive(item.name);
                    if (item.path) navigate(item.path);
                  }}
                  className={`flex items-center gap-2 cursor-pointer transition font-18 leading-[100%] ${
                    isActive
                      ? 'text-[#F0F0F0] border-b border-[#FFFFFF] pb-1'
                      : 'text-[#A7A7A7] hover:text-white'
                  }`}
                >
                  <Icon size={16} />
                  {item.name}
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative" ref={dropdownRef}>
          <div
            onClick={() => setOpenDropdown(!openDropdown)}
            className="hidden lg:flex items-center gap-2 cursor-pointer font-bold"
          >
            Hi, Admin
            <span className="w-8 h-8 flex justify-center items-center rounded-full bg-gray-300">
              <User />{' '}
            </span>
            <ChevronDown size={16} />
          </div>
          <button className="md:hidden" onClick={() => setMobileOpen(true)}>
            <Menu size={22} />
          </button>

          {openDropdown && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute z-50 right-0 mt-2 w-44 bg-white text-black rounded-md shadow-lg py-2 border-2 border-blue-600"
            >
              {/* <div
                onClick={() => !openDropdown}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 "
              >
                <User2 size={18} /> My Profile
              </div> */}
              <div
                onClick={() => navigate('/change-password')}
                className="px-1.5 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 "
              >
                <Key size={18} /> Change Password
              </div>
              <div
                onClick={handleLogout}
                className="px-1.5 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 "
              >
                <LogOut size={18} />
                Logout
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="lg:grid grid-cols-4 mt-6">
        <div className="lg:col-span-3">
          <h1 className="font-bold font-32">{currentPage.title}</h1>

          <p className="font-22 text-[#A7A7A7] mt-1">{currentPage.subtitle}</p>

          {currentPage.type === 'stepper' ? (
            <div className="flex items-center justify-between mt-8 max-w-[1400px]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border flex items-center justify-center">
                  {step > 1 ? (
                    <Check size={12} className="text-green-400" />
                  ) : step === 1 ? (
                    <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  ) : null}
                </div>
                <span className="font-22 font-medium text-white">Basic Details</span>
              </div>

              <div className="flex-1 h-[1px] bg-white mx-4"></div>

              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border flex items-center justify-center">
                  {step === 2 && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
                <span className="font-22 font-medium text-white">Add Candidates</span>
              </div>
            </div>
          ) : (
            currentPage.showButton && (
              <button
                onClick={() => navigate(currentPage.buttonAction)}
                className="mt-8 px-4 py-2 rounded-[38px] font-18 flex items-center gap-2 
                   bg-white/20 backdrop-blur-md 
                   border border-[#DEE4E9] shadow-lg 
                   hover:bg-white/30 transition-all duration-300 mb-4"
              >
                <Calendar size={20} />
                {currentPage.buttonText}
              </button>
            )
          )}
        </div>
        <div className="hidden lg:block relative justify-self-end">
          <div className="absolute inset-0 border border-[#666666] h-[190px] w-[213px]  translate-x-3 -translate-y-3 bg-[#00000033] "></div>
          <img
            src="/images/certificate.png"
            alt="certificate"
            className="max-w-[254px] h-[162px] z-30 relative"
          />
        </div>
      </div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/60" onClick={() => setMobileOpen(false)} />

          <div className="w-[260px] bg-black h-full p-5 border-l border-white/10 ">
            <div className="flex justify-between items-center mb-6">
              Hi, Admin
              <span className="w-8 h-8 flex justify-center items-center rounded-full bg-gray-300">
                <User />
              </span>
            </div>

            <div className="flex flex-col gap-4">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <div
                    key={index}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    className={`flex items-center gap-3 px-1  py-2 rounded-lg cursor-pointer ${
                      isActive ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{item.name}</span>
                  </div>
                );
              })}
              <div
                onClick={handleLogout}
                className="px-1 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3 text-gray-400"
              >
                <LogOut /> Logout
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
